import {
  hasMatchingTimes,
  formatStringForComparison,
} from '../utils/architecture.utils';

/**
 * Performs a bi-directional graph traversal to find all interconnected blocks.
 *
 * It builds an adjacency map where blocks are nodes and `blockReference`/`blockName`
 * relationships are edges. It then uses Breadth-First Search (BFS) to find every
 * block reachable from the `startBlock`, whether it is a parent, child, or sibling.
 *
 * @param {object} startBlock - The normalized block object to start the search from.
 * @param {object[]} allEvents - The pool of events to search within.
 * @returns {object[]} An array containing every block in the interconnected chain.
 */
const findEntireChain = (startBlock, allEvents) => {
  // 1. PRE-PROCESSING: Build an Adjacency Map (Index)
  const adjacencyMap = new Map();

  // Helper to add connections bi-directionally
  const addConnection = (key, block) => {
    if (!key) return;
    if (!adjacencyMap.has(key)) adjacencyMap.set(key, []);
    adjacencyMap.get(key).push(block);
  };

  allEvents.forEach((event) => {
    // Index by the block's own name (so others can find it via reference)
    addConnection(event.blockName, event);
    // Index by the block's reference (so it can find its target)
    addConnection(event.blockReference, event);
  });

  // 2. TRAVERSAL: Use the map
  const queue = [startBlock];
  const visited = new Set([startBlock.id]);
  const entireChain = [];

  while (queue.length > 0) {
    const currentBlock = queue.shift();
    entireChain.push(currentBlock);

    // Get potential neighbors based on Name and Reference
    const potentialNeighbors = [
      ...(adjacencyMap.get(currentBlock.blockName) || []),
      ...(adjacencyMap.get(currentBlock.blockReference) || []),
    ];

    potentialNeighbors.forEach((otherBlock) => {
      if (!visited.has(otherBlock.id)) {
        const isConnected =
          currentBlock.blockReference === otherBlock.blockName ||
          otherBlock.blockReference === currentBlock.blockName;

        if (isConnected) {
          visited.add(otherBlock.id);
          queue.push(otherBlock);
        }
      }
    });
  }

  return entireChain;
};

/**
 * Finds a list of blocks related to a selected block based on configurable business rules.
 *
 * The retrieval logic consists of two main phases:
 * 1. **Core Chain Search**: Finds an interconnected chain of blocks (e.g., Series or TitleGroups).
 *    - By default (`searchEntireSchedule: true`), this searches the full graph to find all linked blocks across the week.
 *    - If `searchEntireSchedule` is false, it restricts the search to blocks within the same Time Slot.
 * 2. **Repeat Search**: Optionally finds all REPEAT blocks that reference any block found in the core chain.
 *
 * @param {object} selected - The raw block object that the user selected. Expects properties
 * like `Id`, `Type`, `StartTime`, `EndTime`, `SeriesName`, etc.
 * @param {object[]} allEvents - An array of all normalized block objects currently present on the schedule.
 * @param {object} [options] - Configuration options to control the search scope.
 * @param {boolean} [options.searchEntireSchedule=true] - Defaults to `true`.
 * - If `true`, performs a full graph traversal to find all linked blocks regardless of their time or day.
 * - If `false`, restricts the search to blocks occurring in the exact same Time Slot (useful for specific slot updates).
 * @param {boolean} [options.requireExactName=true] - Defaults to `true`. 
 * - If `true`, linked blocks are only included if they strictly match the visible name property (e.g., SeriesName) of the selection.
 * - If `false`, blocks are included if they are linked via reference ID, even if their visible names differ (useful for updating groups of loosely linked items).
 * @param {boolean} [options.includeRepeats=true] - Defaults to `true`. If `true`, searches the entire schedule for REPEAT blocks linked to the core results.
 * @param {boolean} [options.includeSelected=false] - Defaults to `false`. If `true`, the returned array includes the originally `selected` block. If `false`, it is filtered out.
 * @returns {object[]} A sorted array (Mon->Sun) of related block objects. Returns an empty array if no related blocks are found.
 */
const findRelatedBlocks = (
  selected,
  allEvents,
  {
    includeRepeats = true,
    includeSelected = false,
    requireExactName = true,
    searchEntireSchedule = true,
  } = {}
) => {
  const types = {
    REPEAT: 'repeat',
    SERIES: 'series',
    TITLEGROUP: 'titlegroup',
    MOVIES: 'movies',
    PLACEHOLDER: 'placeholder',
  };

  // 1. Configuration Map: Define how to match each type
  const typeConfig = {
    [types.SERIES]: { rawProp: 'SeriesName', normProp: 'seriesName' },
    [types.TITLEGROUP]: {
      rawProp: 'TitleGroupName',
      normProp: 'titleGroupName',
    },
    [types.MOVIES]: { rawProp: 'Title', normProp: 'title' },
    [types.PLACEHOLDER]: { rawProp: 'BlockName', normProp: 'blockName' },
  };

  let relatedBlocks = [];

  const selectedType = formatStringForComparison(selected?.Type ?? '');
  const fullSelectedBlock = allEvents.find((block) => block.id === selected.Id);
  if (!fullSelectedBlock) return relatedBlocks;

  // 2. Get the config for the current type
  const config = typeConfig[selectedType];
  let eventsToSearch = allEvents;

  // PRE-FILTER: Scope the search to a specific Time Slot and Entity Type.
  //
  // 1. Time Scope: When updating a "Daily News" Series at 8:00 AM, we only want to affect
  //    other "Daily News" blocks at 8:00 AM. We must exclude the "Daily News" blocks at
  //    6:00 PM, even though they share the same name/reference.
  //
  // 2. Type Scope: We strictly match the Block Type (e.g., Series vs. TitleGroup).
  //    This prevents edge cases where different entity types might coincidentally share
  //    a name and time but should be treated as distinct objects during an update.
  //
  // 3. Name Scope: Controlled by `requireExactName`.
  //    If true, we ensure we only update blocks that share the exact same visible name.
  //    This prevents changes to one Named Entity (e.g., "News") from affecting a different
  //    Named Entity (e.g., "Sports") that happens to be graph-linked to this time slot.
    if (!searchEntireSchedule && selectedType !== types.REPEAT && config) {
    eventsToSearch = allEvents.filter((block) => {
      // A. Check if the names match (SeriesName vs SeriesName, etc.)
      const namesMatch = 
        (block[config.normProp] ?? null) === (selected[config.rawProp] ?? null);

      // B. Determine if we should enforce that match
      // If requireExactName is TRUE, we strictly require namesMatch to be true.
      // If requireExactName is FALSE, we ignore the name mismatch (allow loose graph linking).
      const satisfiesNameRequirement = requireExactName ? namesMatch : true;

      return (
        formatStringForComparison(block.type) === selectedType &&
        satisfiesNameRequirement &&
        hasMatchingTimes(block, {
          startTime: selected.StartTime,
          endTime: selected.EndTime,
        })
      );
    });
  }

  // Step 3: Find the entire interconnected chain starting from the selected block
  // using the scope defined above.
  const coreBlocks = findEntireChain(fullSelectedBlock, eventsToSearch);

  // Step 4: Find any REPEAT blocks that reference any block in the chain.
  let repeatMatches = [];
  if (includeRepeats) {
    const blockNamesToFind = new Set(
      coreBlocks.map((block) => block.blockName)
    );
    repeatMatches = allEvents.filter(
      (block) =>
        formatStringForComparison(block.type) === types.REPEAT &&
        blockNamesToFind.has(block.blockReference)
    );
  }

  // 5. Merge & Deduplicate
  //
  // When `searchEntireSchedule` is true, the `findEntireChain` (BFS) logic naturally finds
  // connected Repeat blocks as part of the graph traversal.
  //
  // However, our explicit `repeatMatches` filter ALSO finds those same blocks to ensure we
  // catch repeats even when the BFS scope is restricted (e.g., during specific time slot updates).
  //
  // Since a block might be found by BOTH methods, we use a Map by ID to squash duplicates
  // and ensure every block appears only once in the final list.
  const uniqueMap = new Map();
  [...coreBlocks, ...repeatMatches].forEach((b) => uniqueMap.set(b.id, b));
  relatedBlocks = Array.from(uniqueMap.values());

  // 6. Sort: Mon(1) -> Sun(7), then by Start Time
  relatedBlocks.sort((a, b) => {
    const dayDiff = (a.dayOfWeek || 0) - (b.dayOfWeek || 0);
    if (dayDiff !== 0) return dayDiff;
    return (a.startTime || '').localeCompare(b.startTime || '');
  });

  // return everything including target block
  if (includeSelected) {
    return relatedBlocks;
  }

  // Ensure the originally selected block is not in the list.
  return relatedBlocks.filter((block) => block.id !== selected.Id);
};

export default findRelatedBlocks;
